import type { ReactNode } from "react";
import {
  Ban,
  ChevronDown,
  EyeOff,
  Layers,
  MessageSquare,
  RotateCcw,
  UserCheck2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RevealProviderButton } from "@/features/provider/reveal-provider-button";
import { useOrganization } from "@/hooks/use-organization";
import { cn } from "@/lib/utils";
import {
  AppliedLabel,
  CandidateStatusBadge,
  LastMessagedLabel,
  NeedsReviewBadge,
} from "./candidate-badges";
import {
  getCandidateDisplayName,
  formatCandidateSubtitle,
  getCandidateFlags,
  getRecentEmployers,
  IMLC_LICENSE_LABEL,
  type Candidate,
  type CandidatePermissions,
  type HiringStage,
  type LapsedRequirement,
} from "./candidate-views";
import {
  CandidateNotesField,
  type CandidateNoteSummary,
} from "./candidate-notes-field";
import type { JobDashboardActions } from "./use-job-dashboard-actions";

const RECENT_EMPLOYER_LIMIT = 2;
const MENU_CONTENT_CLASS_NAME =
  "max-h-[var(--radix-dropdown-menu-content-available-height)] w-56 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl border-slate-200 bg-white p-1.5";
const MENU_ITEM_CLASS_NAME = "min-h-11 cursor-pointer gap-2 rounded-lg";
const NOT_STARTED_LABEL = "Not started";
const EMPTY_FIELD_LABEL = "—";

function CardField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-slate-700 [overflow-wrap:anywhere]">
        {children}
      </dd>
    </div>
  );
}

function formatRecentEmployers(employers: string[]): string {
  if (employers.length === 0) {
    return EMPTY_FIELD_LABEL;
  }
  const shown = employers.slice(0, RECENT_EMPLOYER_LIMIT).join(", ");
  return employers.length > RECENT_EMPLOYER_LIMIT ? `${shown}...` : shown;
}

// IMLC is not identifying, so it shows before reveal like the state chips.
function LicenseChips({ candidate }: { candidate: Candidate }) {
  if (!candidate.hasImlcLicense && candidate.stateLicenses.length === 0) {
    return <span className="text-slate-400">None listed</span>;
  }
  return (
    <span className="flex flex-wrap gap-1">
      {candidate.hasImlcLicense && (
        <span
          className="rounded-md border border-primary/30 bg-primary/5 px-1.5 py-0.5 text-xs font-medium text-primary"
          title="Interstate Medical Licensure Compact"
        >
          {IMLC_LICENSE_LABEL}
        </span>
      )}
      {candidate.stateLicenses.map((state) => (
        <span
          key={state}
          className="rounded-md border border-slate-200 px-1.5 py-0.5 text-xs font-medium text-slate-600"
        >
          {state}
        </span>
      ))}
    </span>
  );
}

function StageMenu({
  candidate,
  stages,
  displayName,
  actions,
}: {
  candidate: Candidate;
  stages: HiringStage[];
  displayName: string;
  actions: JobDashboardActions;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="gap-1.5"
          aria-label={`Move ${displayName} to a stage`}
          disabled={actions.transitionStageMutation.isPending}
        >
          Stage
          <ChevronDown aria-hidden="true" size={14} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        collisionPadding={16}
        className={MENU_CONTENT_CLASS_NAME}
      >
        {stages.map((stage) => (
          <DropdownMenuCheckboxItem
            key={stage.id}
            checked={stage.id === candidate.hiringProcessCurrentStageId}
            onSelect={() =>
              actions.transitionStage({ candidate, stageId: stage.id })
            }
            className="min-h-11 cursor-pointer rounded-lg"
          >
            {stage.name}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DecisionMenu({
  candidate,
  displayName,
  canHire,
  canReject,
  actions,
}: {
  candidate: Candidate;
  displayName: string;
  canHire: boolean;
  canReject: boolean;
  actions: JobDashboardActions;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="gap-1.5"
          aria-label={`Hire or reject ${displayName}`}
        >
          Decision
          <ChevronDown aria-hidden="true" size={14} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        collisionPadding={16}
        className={MENU_CONTENT_CLASS_NAME}
      >
        {canHire && (
          <DropdownMenuItem
            onSelect={() => actions.openHire(candidate)}
            className={MENU_ITEM_CLASS_NAME}
          >
            <UserCheck2
              aria-hidden="true"
              size={15}
              className="text-slate-500"
            />
            Hire
          </DropdownMenuItem>
        )}
        {canReject && (
          <DropdownMenuItem
            onSelect={() => actions.openReject(candidate)}
            className={cn(
              MENU_ITEM_CLASS_NAME,
              "text-red-600 focus:text-red-600",
            )}
          >
            <Ban aria-hidden="true" size={15} className="text-red-500" />
            Reject
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CardFooter({
  candidate,
  stages,
  permissions,
  displayName,
  actions,
}: {
  candidate: Candidate;
  stages: HiringStage[];
  permissions: CandidatePermissions;
  displayName: string;
  actions: JobDashboardActions;
}) {
  const { selectedOrgBusinessUnit } = useOrganization();
  const { isRejected, isWithdrawn, isHired, isHidden, isActive } =
    getCandidateFlags(candidate);
  const canMove =
    permissions.canManageJobPosts &&
    stages.length > 0 &&
    isActive &&
    !isHired &&
    !isHidden;
  const canHire =
    permissions.canManageAffiliations && isActive && !isHired && !isHidden;
  const canReject =
    permissions.canManageJobPosts && !isRejected && !isWithdrawn && !isHidden;
  const canReinstate =
    permissions.canManageJobPosts && isRejected && !isWithdrawn;

  return (
    <div className="mt-auto flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 pt-3">
      {isHidden && permissions.canRevealProviders && (
        <RevealProviderButton
          providerUserId={candidate.userId}
          organizationBusinessUnitId={selectedOrgBusinessUnit.id}
          size="default"
          variant="outline"
        />
      )}
      {!isHidden && !isWithdrawn && (
        // Icon-only keeps Message, Stage and Decision on one row in the
        // two-column grid; the tooltip and aria-label carry the name.
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-11 w-11 shrink-0"
                aria-label={`Message ${displayName}`}
                onClick={() => actions.openChat(candidate)}
              >
                <MessageSquare aria-hidden="true" size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Message</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {canMove && (
        <StageMenu
          candidate={candidate}
          stages={stages}
          displayName={displayName}
          actions={actions}
        />
      )}
      {canReinstate && (
        <Button
          variant="outline"
          className="gap-2"
          disabled={actions.setCandidateRejectionMutation.isPending}
          onClick={() => actions.reinstateCandidate(candidate)}
        >
          <RotateCcw aria-hidden="true" size={16} />
          Reinstate
        </Button>
      )}
      {(canHire || canReject) && (
        <DecisionMenu
          candidate={candidate}
          displayName={displayName}
          canHire={canHire}
          canReject={canReject}
          actions={actions}
        />
      )}
    </div>
  );
}

export function CandidateCard({
  candidate,
  stages,
  lapsed,
  permissions,
  noteSummary,
  actions,
  onOpenDetails,
}: {
  candidate: Candidate;
  stages: HiringStage[];
  lapsed: LapsedRequirement[];
  permissions: CandidatePermissions;
  noteSummary: CandidateNoteSummary | undefined;
  actions: JobDashboardActions;
  onOpenDetails: (candidate: Candidate) => void;
}) {
  const flags = getCandidateFlags(candidate);
  const { isRejected, isWithdrawn, isHidden, isActive } = flags;
  const displayName = getCandidateDisplayName(candidate);
  const subtitle = formatCandidateSubtitle(candidate);
  const currentStage = stages.find(
    (stage) => stage.id === candidate.hiringProcessCurrentStageId,
  );
  const hasStages = stages.length > 0;
  const specialtyNames = (candidate.specialties ?? []).map(
    (specialty) => specialty.name,
  );
  const showLapsed =
    permissions.canManageJobPosts && isActive && lapsed.length > 0;

  return (
    <article
      className={cn(
        "flex min-w-0 flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-xs sm:p-5",
        (isRejected || isWithdrawn) && "bg-slate-50/60",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          {isHidden && (
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-100">
              <EyeOff aria-hidden="true" className="h-5 w-5 text-slate-400" />
            </div>
          )}
          <div className="min-w-0">
            <h3 className="text-base font-semibold leading-6 text-slate-900 [overflow-wrap:anywhere]">
              <button
                type="button"
                onClick={() => onOpenDetails(candidate)}
                className="rounded-md text-left hover:text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
              >
                {displayName}
              </button>
            </h3>
            {subtitle && (
              <p className="text-sm leading-5 text-slate-500 [overflow-wrap:anywhere]">
                {subtitle}
              </p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5 text-right">
          <CandidateStatusBadge flags={flags} />
          <AppliedLabel interestedAt={candidate.interestedAt} />
          <LastMessagedLabel
            lastMessage={candidate.lastMessage}
            lastOrgMessageAt={candidate.lastOrgMessageAt}
          />
        </div>
      </div>

      {/* Unrevealed providers still show professional details; only
          identifying information (name, employers) stays masked. */}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
        <CardField label="Specialty">
          {specialtyNames.length > 0
            ? specialtyNames.join(", ")
            : EMPTY_FIELD_LABEL}
        </CardField>
        {hasStages && !isHidden && (
          <CardField label="Hiring step">
            <span className="inline-flex items-center gap-1.5 font-medium text-primary">
              <Layers aria-hidden="true" size={13} className="shrink-0" />
              {currentStage?.name ?? NOT_STARTED_LABEL}
            </span>
          </CardField>
        )}
        <CardField label="Licenses">
          <LicenseChips candidate={candidate} />
        </CardField>
        {!isHidden && (
          <CardField label="Recent employers">
            {formatRecentEmployers(getRecentEmployers(candidate))}
          </CardField>
        )}
        {permissions.canManageJobPosts && (
          <CandidateNotesField
            summary={noteSummary}
            displayName={displayName}
            onOpen={() => actions.openNotes(candidate)}
          />
        )}
      </dl>

      {showLapsed && (
        <div>
          <NeedsReviewBadge count={lapsed.length} />
        </div>
      )}

      <CardFooter
        candidate={candidate}
        stages={stages}
        permissions={permissions}
        displayName={displayName}
        actions={actions}
      />
    </article>
  );
}
